import { useTranslation } from '@/utils/hook'
import Image from 'next/image'
import { RiArrowRightLine } from '@remixicon/react'
import { libreBaskerville } from '@/fonts'
import { SectionContainer } from '@/components/ui'

export function Banner({ githubStars, userCount }): JSX.Element {
  const t = useTranslation()
  const formattedGithubStars = new Intl.NumberFormat().format(githubStars)
  const roundedUserCount =
    userCount >= 1000 ? `${Math.round(userCount / 1000)}k` : `${userCount}`

  return (
    <section
      aria-labelledby="home-hero-title"
      className="relative isolate overflow-hidden bg-[rgb(var(--ds-surface))] pt-32 sm:pt-36 lg:pt-40"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(180deg,transparent_20%,rgb(var(--ds-accent-soft))_100%)]"
      />
      <SectionContainer>
        <div className="mx-auto max-w-[54rem] text-center">
          <h1
            id="home-hero-title"
            className={`${libreBaskerville.className} text-balance text-[clamp(1.875rem,5.1vw,4rem)] font-bold leading-[1.12] tracking-[-0.055em] text-[rgb(var(--ds-text))]`}
          >
            {t('home.bannerHeadline1')}
          </h1>
          <p className="mx-auto mt-6 max-w-[35rem] text-pretty text-base leading-relaxed text-[rgb(var(--ds-text-muted))] sm:text-lg">
            Build interactive forms, own your data, and stop overpaying.
          </p>
          <div className="mt-7 flex justify-center sm:mt-8">
            <a
              className="group inline-flex min-h-12 items-center justify-center gap-3 whitespace-nowrap rounded-full bg-slate-950 px-6 py-3.5 text-base font-medium text-white shadow-[0_6px_18px_rgba(15,23,42,0.12)] transition-colors hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600 active:bg-slate-700"
              href="https://my.heyform.net/sign-up"
            >
              <span>{t('common.tryFree')}</span>
              <RiArrowRightLine
                className="h-5 w-5 motion-safe:transition-transform motion-safe:group-hover:translate-x-1"
                aria-hidden="true"
              />
            </a>
          </div>
          <div
            aria-label="HeyForm ratings and community"
            className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-[rgb(var(--ds-text-muted))] sm:text-sm"
          >
            <div className="flex items-center gap-2.5">
              <Image
                src="/static/stars-4.5.svg"
                alt="4.5 out of 5 stars"
                width={180}
                height={32}
                className="h-4 w-auto"
              />
              <span>
                <strong className="font-medium text-[rgb(var(--ds-text))]">
                  4.5
                </strong>{' '}
                rating
              </span>
            </div>
            {githubStars > 0 && (
              <a
                href="https://github.com/heyform/heyform"
                className="rounded-sm underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600"
              >
                <strong className="font-medium tabular-nums text-[rgb(var(--ds-text))]">
                  {formattedGithubStars}
                </strong>{' '}
                GitHub stars
              </a>
            )}
            {userCount > 0 && (
              <span>
                <strong className="font-medium tabular-nums text-[rgb(var(--ds-text))]">
                  {roundedUserCount}+
                </strong>{' '}
                users
              </span>
            )}
          </div>
        </div>
        <div className="mx-auto mt-9 max-w-[66rem] sm:mt-10 lg:mt-12">
          <Image
            src="/static/banner.webp"
            alt="HeyForm form builder with questions, a live form preview, and design settings"
            className="h-auto w-full"
            sizes="(max-width: 1152px) 100vw, 1056px"
            width={2884}
            height={1786}
            priority
            unoptimized
          />
        </div>
      </SectionContainer>
    </section>
  )
}
